import {AssetPartSignal} from '@domain/kiln-monitoring'
import {Done} from '@mui/icons-material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {Alert, Box, List, ListItem, ListItemIcon, ListItemText, Typography} from '@mui/material'
import {styled} from '@mui/system'
import {isArray} from 'lodash'

import {useConnectedSignals} from '../../hooks'
import {useKilnConfig} from '../../providers'

import {getIsKilnConfigValid} from '@kilnfeature/helpers'

const StyledWarningListItem = styled(ListItem)(() => ({
  backgroundColor: 'rgb(253, 243, 229)',
  color: 'rgb(95, 55, 0)',
  borderRadius: '8px',
  marginBottom: '8px'
}))

const StyledWarningIcon = styled(ListItemIcon)(({theme}) => ({
  color: theme.palette.warning.main,
  minWidth: 30
}))

const StyledCompletedListItem = styled(ListItem)(() => ({
  backgroundColor: 'rgb(229, 246, 237)',
  color: 'rgb(0, 68, 32)',
  borderRadius: '8px',
  marginBottom: '8px'
}))

const StyledCompletedIcon = styled(ListItemIcon)(({theme}) => ({
  color: theme.palette.success.main,
  minWidth: 30
}))

const renderConditionalItem = (isSuccess: boolean, title: string, subtitle?: string) => {
  return isSuccess ? (
    <StyledCompletedListItem data-test-id="kiln-setup-step">
      <StyledCompletedIcon data-test-id="completed-icon">
        <Done />
      </StyledCompletedIcon>
      <ListItemText primary={title} />
    </StyledCompletedListItem>
  ) : (
    <StyledWarningListItem data-test-id="kiln-setup-step">
      <StyledWarningIcon data-test-id="warning-icon">
        <WarningAmberIcon />
      </StyledWarningIcon>
      <ListItemText primary={title} secondary={subtitle} />
    </StyledWarningListItem>
  )
}

export const KilnConfigSettingsSteps = () => {
  const {kilnLayout, kilnConfiguration} = useKilnConfig()
  const {
    details: {
      isKilnNameSetUp,
      areKilnDimensionsSetUp,
      isDriveNameSetUp,
      areDriveBoundariesSetUp,
      isMoreThanOneStation,
      areStationsNamesSetUp,
      areStationsBoundariesSetUp
    }
  } = getIsKilnConfigValid(kilnLayout, kilnConfiguration)

  const connectedSignals = useConnectedSignals({
    selectedSignals: kilnLayout.selectedSignals,
    kilnPartId: kilnLayout.kilnShell.id
  })
  const speedSignals = kilnLayout.speedSignals ?? []
  const feedSignals = kilnLayout.feedSignals ?? []

  const connectedThrustRoller = kilnLayout.stations
    .map((s) => s.thrustRollerPosition)
    .reduce((v, s) => (!v ? s !== 'none' : true), false)

  const stationsSignals =
    kilnLayout.stations.length > 0 &&
    kilnLayout.stations
      .map((s) =>
        Object.keys(kilnLayout.selectedSignals)
          .filter((key) => key.startsWith(s.id))
          .flatMap((key) =>
            kilnLayout.selectedSignals[key].map((signal: AssetPartSignal) => signal.id)
          )
          .filter(Boolean)
      )
      .reduce((v, s) => (v ? isArray(s) && s.length > 0 : false), true)

  const driveSignals =
    Object.keys(kilnLayout.selectedSignals)
      .filter((key) => key.startsWith(kilnLayout.drive.id))
      .flatMap((key) => kilnLayout.selectedSignals[key].map((signal: AssetPartSignal) => signal.id))
      .filter(Boolean).length > 0

  return (
    <Box ml={5} p={2}>
      <Box mb={2}>
        <Typography variant={'h4'}>Kiln Setup Progress</Typography>
      </Box>

      <List>
        {/* General Kiln */}
        <ListItem sx={{padding: 0, marginBottom: '8px'}}>
          <Typography variant="h5">General Kiln</Typography>
        </ListItem>
        <ListItem sx={{padding: 0, marginBottom: '8px'}}>
          <Alert severity={'info'} sx={{width: '100%'}}>
            Check Kiln Rotation
          </Alert>
        </ListItem>
        {renderConditionalItem(
          isKilnNameSetUp && areKilnDimensionsSetUp,
          'Base Kiln information',
          '(name, length, diameter)'
        )}
        {renderConditionalItem(connectedSignals.length > 0, 'General Kiln signals')}
        {renderConditionalItem(speedSignals.length > 0, 'Kiln Speed signals')}
        {renderConditionalItem(feedSignals.length > 0, 'Kiln Feed signals')}

        {/* Drive */}
        <ListItem sx={{padding: 0, marginBottom: '8px'}}>
          <Typography variant="h5">Drive</Typography>
        </ListItem>
        <ListItem sx={{padding: 0, marginBottom: '8px'}}>
          <Alert severity={'info'} sx={{width: '100%'}}>
            Check Drive placement
          </Alert>
        </ListItem>
        {renderConditionalItem(isDriveNameSetUp, 'Drive name')}
        {renderConditionalItem(areDriveBoundariesSetUp, 'Drive boundaries')}
        {renderConditionalItem(driveSignals, 'Drive signals')}

        {/* Stations */}
        <ListItem sx={{padding: 0, marginBottom: '8px'}}>
          <Typography variant="h5">Stations</Typography>
        </ListItem>
        <ListItem sx={{padding: 0, marginBottom: '8px'}}>
          <Alert severity={'info'} sx={{width: '100%'}}>
            Check Stations placement
          </Alert>
        </ListItem>
        {renderConditionalItem(isMoreThanOneStation, 'At least 2 Stations')}
        {renderConditionalItem(areStationsNamesSetUp, 'Stations names')}
        {renderConditionalItem(areStationsBoundariesSetUp, 'Stations boundaries')}
        {renderConditionalItem(stationsSignals, 'Stations signals')}
        {renderConditionalItem(connectedThrustRoller, 'Thrust Rollers')}
      </List>
    </Box>
  )
}
